import { is, List } from "immutable";
import _ from "lodash";
import moment from "moment";
import * as QueryString from "querystringify";
import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { CareerCenterJobCategories } from "../../../domain";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";
import { SearchBox } from "../components";
import { CareerCenterHelper } from "../../../models/careerCenter/CareerCenterHelper";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
    countries: state.countries.countries,
    resumes: state.careerCenter.resumes,
    loadingData: state.careerCenter.loadingResumes,
    isLoggedIn: state.auth.data !== undefined && state.auth.data.hasUserRecord,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    loadData: () => dispatch(Actions.CareerCenter.loadResumes()),
    toggleLoading: (loading: boolean) => dispatch(Actions.CareerCenter.toggleResumesLoading(loading)),
    updateQuery: (query: UrlQuery, overwrite?: boolean) => dispatch(Actions.Router.updateQueryParams(query, overwrite)),
});

interface UrlQuery {
    cat?: string;
    q?: string;
    page?: string;
}

type OwnProps = RouteComponentProps<{
}> & {
};

interface ConnectedState {
    api: Api;
    countries: List<M.Country.Country>;
    resumes: List<M.CareerCenter.Resume>;
    loadingData: boolean;
    isLoggedIn: boolean;
}

interface ConnectedDispatch {
    loadData: () => void;
    toggleLoading: (loading: boolean) => void;
    updateQuery: (query: UrlQuery, overwrite?: boolean) => void;
}

interface BrowseType {
    name: string;
    param: string;
    toggled: boolean;
}

interface OwnState {
    appliedFilters: M.Search.AppliedFilter[];
    doFilter: boolean;
    errors: string[];
    filteredData: List<M.CareerCenter.Resume>;
    filters: {
        category: BrowseType;
        location: BrowseType;
    };
    scrollTop?: number;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ResumeDirectoryComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _searchTimer: NodeJS.Timer | undefined;
    private _itemsPerPage = 10;

    public constructor(props: any) {
        super(props);
        this.state = {
            appliedFilters: [],
            doFilter: false,
            errors: [],
            filteredData: List(),
            filters: {
                category: { name: "category", param: "cat", toggled: false },
                location: { name: "location", param: "loc", toggled: false },
            },
            loading: false,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        if (this.props.resumes.size) {
            this.filterData(this.props.resumes);
            this.onQueryUpdated(this.props.location.search);
        }
        else
            this.props.toggleLoading(true);

        await this.props.loadData();

        if (!this._isMounted)
            return;

        this.onQueryUpdated(this.props.location.search);
    }

    public componentDidUpdate(prevProps: CombinedProps, prevState: OwnState) {
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public componentWillReceiveProps(nextProps: Readonly<CombinedProps>, nextContext: any) {
        if (!is(this.props.resumes, nextProps.resumes))
            this.triggerDataFilter();
    }

    public shouldComponentUpdate?(nextProps: Readonly<CombinedProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {

        if (nextState.doFilter) {
            this.filterData(nextProps.resumes);
            return false;
        }

        if (nextProps.location.search !== this.props.location.search) {
            this.onQueryUpdated(nextProps.location.search);
            return false;
        }

        return true;
    }

    public render() {
        const currentUrl = this.props.match.url + this.props.location.search;
        const query = this.getUrlQuery();
        const errors = this.state.errors.length
            ? <BS.Alert bsStyle="warning">
                <h3>Failed to display resume directory</h3>
                <ul>
                    {this.state.errors.map((e, i) => (<li key={i}>{e.toString()}</li>))}
                </ul>
            </BS.Alert>
            : null;

        // create applied filters
        const appliedFilters = <Comp.Search.AppliedFilters
            filters={this.state.appliedFilters}
            onClearAll={() => this.onClearAllFilters()}
            onClearFilter={(id, type) => this.onClearFilter(id, type)}
        />;

        // login note
        const loginNote = this.props.isLoggedIn
            ? null
            : <BS.Alert bsStyle="info">
                <h3>Login required</h3>
                <p><Link to={Paths.Helper.Login(this.props.match.url)}>Please sign in</Link> to view resume profiles.</p>
            </BS.Alert>;

        // build resume lists
        const page = parseInt(query.page || "1", 10);
        const resumeList = this.state.filteredData;
        const numOfLists = Math.ceil(resumeList.size / this._itemsPerPage);
        const resumeListElements: JSX.Element[] = [];

        for (let i = 1; i <= numOfLists; i++) {
            const minIdx = (i - 1) * this._itemsPerPage;
            const maxIdx = Math.min((i * this._itemsPerPage) - 1, resumeList.size - 1);
            let subList: JSX.Element | JSX.Element[] = [];

            for (let k = minIdx; k <= maxIdx; k++) {
                const resume = resumeList.get(k);

                subList.push(<li key={k} id={"resume-" + resume.id}>
                    <Preview currentUrl={currentUrl} isLoggedIn={this.props.isLoggedIn} resume={resume} />
                </li>);
            }

            subList = <BS.Collapse key={i} in={i <= page} timeout={625}>
                <ul className={"jobs" + (i > 1 ? " more" : "")}>{subList}</ul>
            </BS.Collapse>;
            resumeListElements.push(subList);
        }

        const showMore = page < numOfLists
            ? <BS.Button bsStyle="primary" className="center-block" onClick={() => this.onMoreClick()}>Show more&hellip;</BS.Button>
            : null;

        return <Comp.Page.PublicPage additionalClasses="full" pageTitle="Search Resumes" style={{ minHeight: "200px", position: "relative" }}>
            <Comp.Reusable.CountriesLoader />
            <Comp.Generic.LoadingSpinner show={this.state.loading || this.props.loadingData} />
            <Comp.Generic.WindowMotionScroll scrollTop={this.state.scrollTop} />

            <div id="content-primary">
                {errors}

                <SearchBox query={query.q} onSearch={(q) => this.onSearchChange(q)} />

                <h2>Browse</h2>

                <div className="browse">
                    <ul>
                        {CareerCenterJobCategories.Values.map((x, i) => (
                            <li key={i}>
                                <a href="#" onClick={(e) => this.onFilterClick(e, this.state.filters.category, x.id)}>{x.name}</a>
                            </li>
                        ))}
                    </ul>
                </div>

                {appliedFilters}

                <hr />

                <h2>Most recent</h2>

                {loginNote}

                {resumeListElements}

                {showMore}
            </div>
            <div id="content-secondary"></div>
            <div className="clear"></div>
        </Comp.Page.PublicPage>;
    }

    private filterData(resumes: List<M.CareerCenter.Resume>) {
        let filteredData: List<M.CareerCenter.Resume> = List();
        const urlQuery = this.getUrlQuery();

        const page = parseInt(urlQuery.page || "1", 10);
        const search = decodeURIComponent(urlQuery.q || "").toLowerCase();
        const catIds = (urlQuery.cat || ",").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));

        _.orderBy(resumes.toArray(), ["postedOn"], ["desc"]).map((x, i) => {
            if (x) {
                // filter by category
                const catOK = !catIds.length || catIds.indexOf(x.categoryId) > -1;

                // filter by search
                let searchOK = !search || search.length < 2;
                if (catOK && search && search.length >= 2) {
                    const t = (x.title || "").toLowerCase();
                    const c = (Utility.stripHtml(x.coverLetter) || "").replace(/\r\n/, " ").toLowerCase();
                    searchOK = t === search || t.indexOf(search + " ") > -1 || t.indexOf(" " + search) > -1 || t.startsWith(search) || t.endsWith(search)
                        || c === search || c.indexOf(search + " ") > -1 || c.indexOf(" " + search) > -1 || c.startsWith(search) || c.endsWith(search);
                }

                if (catOK && searchOK) {
                    filteredData = filteredData.push(x);
                }
            }
        });

        // make sure page is within range
        const pages = Math.ceil(filteredData.size / this._itemsPerPage);
        if (pages > 0 && page < 1)
            this.props.updateQuery({ page: "" });
        else if (pages > 0 && page > pages)
            this.props.updateQuery({ page: pages.toString() });
        else {
            this.setState({
                ...this.state,
                doFilter: false,
                filteredData,
            });
        }
    }

    private getUrlQuery(): UrlQuery {
        return QueryString.parse(this.props.location.search) as UrlQuery;
    }

    private onClearAllFilters() {
        this.props.updateQuery({ q: "", cat: "" });
    }

    private onClearFilter(id: string | number, type: string) {
        const urlQuery = this.getUrlQuery();
        const items = Utility.commaStringToArray(urlQuery[type]);
        Utility.addToOrRemoveFromArray(items, id, false);
        this.props.updateQuery({ [type]: items.join(",") });
    }

    /**
     * Adds the given browse filter ID to the URL params
     * @param e Mouse click event
     * @param type Browse type
     * @param value Browse filter value
     */
    private onFilterClick(e: React.MouseEvent<HTMLAnchorElement>, type: BrowseType, value: any) {
        const filters = { ...this.state.filters };
        (filters as any)[type.name].toggled = false;
        this.setState({ ...this.state, filters });
        this.props.updateQuery({ [type.param]: value, page: "" });
        e.preventDefault();
        return false;
    }

    private onMoreClick() {
        const urlQuery = this.getUrlQuery();
        const page = parseInt(urlQuery.page || "1", 10);
        this.props.updateQuery({ page: (page + 1).toString() });
    }

    /**
     * Resets all filter checks based on current url query
     */
    private onQueryUpdated(query?: string) {
        const appliedFilters: M.Search.AppliedFilter[] = [];
        const urlQuery = QueryString.parse((query || "")) as UrlQuery;
        const search = decodeURIComponent(urlQuery.q || "");
        const catIds = (urlQuery.cat || ",").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));

        if (search || search.length >= 2)
            appliedFilters.push({
                id: search,
                name: search,
                type: "q",
                typeName: "Keyword",
            });

        if (catIds.length) {
            for (const cid of catIds) {
                const cat = _.find(CareerCenterJobCategories.Values, (c) => c.id === cid);
                if (cat)
                    appliedFilters.push({
                        id: cat.id.toString(),
                        name: cat.name,
                        type: "cat",
                        typeName: "Resume category",
                    });
            }
        }

        this.setState({
            ...this.state,
            appliedFilters,
            doFilter: true,
        }, () => {
            this.setState({...this.state, doFilter: true});
        });
    }

    private onSearchChange(query: string) {
        if (this._searchTimer !== undefined)
            clearTimeout(this._searchTimer);

        this._searchTimer = setTimeout(() => {
            this.props.updateQuery({ page: "", q: (query || "").length < 2 ? "" : query });
        }, 375);
    }

    private triggerDataFilter() {
        this.setState({ ...this.state, doFilter: true });
    }
}

export const ResumeDirectory: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ResumeDirectoryComponent) as any);

interface PreviewProps {
    currentUrl: string;
    isLoggedIn: boolean;
    resume: M.CareerCenter.Resume;
}

const Preview: React.SFC<PreviewProps> = (props) => {
    const resume = props.resume;
    const isMemberDisplay = resume.postedBy.isMember
        ? <li><strong><img className="asrs-eye" src={`${process.env.PUBLIC_URL}/content/images/asrs-eye.svg`} /> ASRS Member</strong></li>
        : null;
    const desc = (Utility.decodeHtml(resume.coverLetter) || "").replace(/\r\n/, " ");
    const latestEducation = _.maxBy(resume.educations, (e) => e.completedOn);
    const latestEdDisplay = latestEducation !== undefined
        ? <li>{latestEducation.type}: {latestEducation.organization}</li>
        : null;
    const title = props.isLoggedIn
        ? <Link to={{ pathname: CareerCenterHelper.resumeDetailsLink(resume), state: { prevPath: props.currentUrl } }}>{resume.title}</Link>
        : resume.title;

    return <Comp.Generic.WrapperLess>
        <h3 className="jobs-title">
            {title}
        </h3>

        <ul className="jobs-summary">
            <li>Posted {moment(Utility.fromApiDate(resume.postedOn)).format("MMM D, YYYY")}</li>
            {isMemberDisplay}
            {latestEdDisplay}
        </ul>

        <div className="jobs-preview" dangerouslySetInnerHTML={{ __html: desc.length > 200 ? (desc.substr(0, 200).trim() + "&hellip;") : desc }} />
    </Comp.Generic.WrapperLess>;
};



// WEBPACK FOOTER //
// ./src/containers/careerCenter/resumeDirectory/ResumeDirectory.tsx